import { createTheme } from '@mui/material/styles';

/**
 * FAIM2 MUI theme — bridges MUI components to our CSS custom property tokens.
 *
 * Uses `var(--token)` references so the theme automatically follows
 * .theme-light / .theme-dark without JS re-renders.
 */
export const faim2Theme = createTheme({
  palette: {
    primary: {
      main: '#004e2b',
      dark: '#003d22',
      light: '#ccddd5',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#4e565e',
      dark: '#3e454b',
      light: '#e6eaed',
    },
    error: {
      main: '#db2e74',
      dark: '#c4286a',
      light: '#f8d5e3',
    },
    warning: {
      main: '#ec9f18',
      dark: '#d38e15',
      light: '#fbe8c5',
    },
    info: {
      main: '#1a71c7',
      dark: '#155a9f',
      light: '#d1e3f4',
    },
    success: {
      main: '#004e2b',
      dark: '#003d22',
      light: '#ccddd5',
    },
    text: {
      primary: 'rgba(0, 0, 0, 0.87)',
      secondary: '#7b7b7b',
    },
    background: {
      default: '#f2f2f2',
      paper: '#ffffff',
    },
    divider: '#dee2e6',
  },
  typography: {
    fontFamily: 'var(--hm-font-body)',
    h1: { fontFamily: 'var(--hm-font-display)', fontSize: '1.5rem', fontWeight: 700 },
    h2: { fontFamily: 'var(--hm-font-display)', fontSize: '1.25rem', fontWeight: 700 },
    body1: { fontSize: '0.875rem' },
    body2: { fontSize: '0.8125rem' },
  },
  shape: {
    borderRadius: 6,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          color: 'var(--foreground)',
          backgroundColor: 'var(--page_background)',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
        variant: 'outlined',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: 'var(--foreground)',
          backgroundColor: 'var(--container_background)',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--border)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--border_hover)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--primary)',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: 'var(--foreground_faded)',
          '&.Mui-focused': {
            color: 'var(--primary)',
          },
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        icon: {
          color: 'var(--foreground_faded)',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: 'var(--container_background)',
          color: 'var(--foreground)',
          borderColor: 'var(--border)',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'var(--container_background_hover)',
          },
          '&.Mui-selected': {
            backgroundColor: 'var(--container_background_selected)',
            '&:hover': {
              backgroundColor: 'var(--container_background_hover)',
            },
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontFamily: 'var(--hm-font-body)',
          fontWeight: 'var(--hm-weight-medium)',
          '&:focus-visible': {
            outline: '2px solid var(--hm-color-deep-green)',
            outlineOffset: 2,
          },
        },
        sizeSmall: {
          height: 36,
        },
        contained: {
          backgroundColor: 'var(--primary)',
          color: 'var(--foreground_inverted)',
          '&:hover': {
            backgroundColor: 'var(--primary_hover)',
          },
        },
        outlined: {
          borderColor: 'var(--border)',
          color: 'var(--foreground)',
          '&:hover': {
            borderColor: 'var(--border_hover)',
            backgroundColor: 'var(--container_background_hover)',
          },
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          color: 'var(--foreground_faded)',
          '&.Mui-checked': {
            color: 'var(--primary)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          '& .MuiChip-label': {
            color: 'inherit',
          },
          '&.MuiChip-outlined.MuiChip-colorPrimary': {
            backgroundColor: 'var(--primary_faded_20)',
            borderColor: 'var(--primary)',
            color: 'var(--primary)',
            '&:hover': {
              backgroundColor: 'var(--primary_faded_20_hover)',
            },
          },
          '&.MuiChip-outlined.MuiChip-colorSuccess': {
            backgroundColor: 'var(--positive_faded_20)',
            borderColor: 'var(--positive)',
            color: 'var(--positive)',
          },
          '&.MuiChip-outlined.MuiChip-colorError': {
            backgroundColor: 'var(--negative_faded_20)',
            borderColor: 'var(--negative)',
            color: 'var(--negative)',
          },
        },
      },
    },
    MuiCard: {
      defaultProps: {
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          backgroundColor: 'var(--container_background)',
          borderColor: 'var(--border)',
          color: 'var(--foreground)',
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--container_background)',
          color: 'var(--foreground)',
          border: '1px solid var(--border)',
          '&:before': {
            display: 'none',
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          '& .MuiAccordionSummary-expandIconWrapper': {
            color: 'var(--foreground_faded)',
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          backgroundColor: 'var(--container_background_elevated)',
          color: 'var(--foreground)',
          border: '1px solid var(--border)',
        },
        option: {
          '&.Mui-focused': {
            backgroundColor: 'var(--container_background_hover)',
          },
          '&[aria-selected="true"]': {
            backgroundColor: 'var(--container_background_selected)',
          },
        },
        popupIndicator: {
          color: 'var(--foreground_faded)',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: 'var(--foreground)',
          '&.MuiTypography-colorTextSecondary': {
            color: 'var(--foreground_faded)',
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          '&.MuiAlert-standardError': {
            backgroundColor: 'var(--negative_faded_20)',
            color: 'var(--negative)',
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          color: 'var(--foreground)',
        },
      },
    },
  },
});
